import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const CHANNEL_COUNT = 1000
const CHANNEL_SIZE = 64
const MEMORY_SIZE = CHANNEL_COUNT * CHANNEL_SIZE
const IDENTIFIERS = new Set(['DR735TN', 'DR735TE'])
const MODE_BY_INDEX: Readonly<Record<number, string>> = {
  0x00: 'FM',
  0x01: 'NFM',
  0x02: 'AM',
  0x03: 'NAM',
  0x80: 'Auto',
}
const MODE_INDEX = new Map(
  Object.entries(MODE_BY_INDEX).map(([index, mode]) => [mode, Number(index)]),
)
const ALINCO_TONES: readonly number[] = CTCSS_TONES.filter(
  (tone) =>
    ![
      159.8, 165.5, 171.3, 177.3, 183.5, 189.9, 196.6, 199.5, 206.5, 229.1,
      254.1,
    ].includes(tone),
)
const DCS_CODES: readonly number[] = DTCS_CODES

export type DR735TProfile = {
  protocol: 'alinco-dr735t'
  id: string
  vendor: 'Alinco'
  model: 'DR735T'
  summary: string
  tags: readonly string[]
  baudRate: 38400
  channelMinimum: 0
  channelMaximum: 999
  nameLength: 6
  memorySize: number
  powerLevels: readonly ['High', 'Mid', 'Low']
}

export const DR735T_PROFILES: readonly DR735TProfile[] = [
  {
    protocol: 'alinco-dr735t',
    id: 'alinco-dr735t',
    vendor: 'Alinco',
    model: 'DR735T',
    summary: 'Alinco DR735T dual-band mobile.',
    tags: ['Experimental', 'Read + write', '1,000 channels', '6-character names'],
    baudRate: 38400,
    channelMinimum: 0,
    channelMaximum: 999,
    nameLength: 6,
    memorySize: MEMORY_SIZE,
    powerLevels: ['High', 'Mid', 'Low'],
  },
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'High',
    skip: false,
  }
}

function readUint32(raw: Uint8Array, offset: number): number {
  return (
    (raw[offset] |
      (raw[offset + 1] << 8) |
      (raw[offset + 2] << 16) |
      (raw[offset + 3] << 24)) >>>
    0
  )
}

function writeUint32(raw: Uint8Array, offset: number, value: number): void {
  raw[offset] = value & 0xff
  raw[offset + 1] = (value >> 8) & 0xff
  raw[offset + 2] = (value >> 16) & 0xff
  raw[offset + 3] = (value >> 24) & 0xff
}

function decodeName(raw: Uint8Array): string {
  let name = ''
  for (const value of raw) {
    if (value === 0x00 || value === 0xff) break
    if (value >= 0x30 && value <= 0x39) name += String.fromCharCode(value)
    if (value >= 0x41 && value <= 0x5a) name += String.fromCharCode(value)
    if (value === 0x5b) name += ' '
  }
  return name.trim()
}

function encodeName(name: string): Uint8Array {
  const encoded = new Uint8Array(6).fill(0x5b)
  Array.from(name.toUpperCase().padEnd(6).slice(0, 6)).forEach(
    (character, index) => {
      const code = character.charCodeAt(0)
      encoded[index] =
        (code >= 0x30 && code <= 0x39) ||
        (code >= 0x41 && code <= 0x5a) ||
        code === 0x5b
          ? code
          : 0x5b
    },
  )
  return encoded
}

export function parseDR735TChannel(
  image: Uint8Array,
  profile: DR735TProfile,
  number: number,
): WebChannel {
  if (number < profile.channelMinimum || number > profile.channelMaximum) {
    return emptyChannel(number)
  }
  const offset = number * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] !== 0x55) {
    return emptyChannel(number)
  }

  const rxFrequency = readUint32(raw, 4)
  const shift = readUint32(raw, 8)
  const txFrequency =
    raw[12] === 1
      ? rxFrequency - shift
      : raw[12] === 2
        ? rxFrequency + shift
        : rxFrequency
  const toneMode =
    raw[13] === 0x0c ? 'dtcs' : raw[13] === 0 ? 'none' : 'ctcss'
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(24, 30)),
    rxFrequency,
    txFrequency,
    toneMode,
    ctcss: toneMode === 'dtcs' ? null : ALINCO_TONES[raw[15]] ?? null,
    dtcs: toneMode === 'dtcs' ? DCS_CODES[raw[16]] ?? null : null,
    dtcsPolarity: 'N',
    mode: MODE_BY_INDEX[raw[20]] ?? 'FM',
    power: profile.powerLevels[raw[18]] ?? profile.powerLevels[0],
    skip: raw[1] !== 0,
  }
}

export function parseDR735TChannels(
  image: Uint8Array,
  profile: DR735TProfile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, number) =>
    parseDR735TChannel(image, profile, number),
  )
}

export function applyDR735TChannel(
  image: Uint8Array,
  profile: DR735TProfile,
  channel: WebChannel,
): void {
  if (
    channel.number < profile.channelMinimum ||
    channel.number > profile.channelMaximum
  ) {
    throw new Error('Channel must be between 0 and 999.')
  }
  const offset = channel.number * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) {
    throw new Error('The selected image is smaller than DR735T memory.')
  }
  if (channel.empty) {
    raw.fill(0)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')

  const txFrequency = channel.txFrequency ?? channel.rxFrequency
  const toneIndex =
    channel.toneMode === 'ctcss'
      ? ALINCO_TONES.indexOf(channel.ctcss ?? ALINCO_TONES[0])
      : 0
  const dtcsIndex =
    channel.toneMode === 'dtcs'
      ? DCS_CODES.indexOf(channel.dtcs ?? DCS_CODES[0])
      : 0
  if (toneIndex < 0) {
    throw new Error('That CTCSS tone is not supported by this radio.')
  }
  if (dtcsIndex < 0) {
    throw new Error('That DCS code is not supported by this radio.')
  }
  const powerIndex = profile.powerLevels.indexOf(
    channel.power as (typeof profile.powerLevels)[number],
  )
  const modeIndex = MODE_INDEX.get(channel.mode)
  if (modeIndex == null) {
    throw new Error('That modulation mode is not supported by this radio.')
  }

  raw.fill(0)
  raw[0] = 0x55
  raw[1] = channel.skip ? 1 : 0
  writeUint32(raw, 4, channel.rxFrequency)
  writeUint32(raw, 8, Math.abs(txFrequency - channel.rxFrequency))
  raw[12] =
    txFrequency < channel.rxFrequency ? 1 : txFrequency > channel.rxFrequency ? 2 : 0
  raw[13] =
    channel.toneMode === 'dtcs' ? 0x0c : channel.toneMode === 'ctcss' ? 1 : 0
  raw[14] = toneIndex
  raw[15] = toneIndex
  raw[16] = dtcsIndex
  raw[18] = Math.max(0, powerIndex)
  raw[20] = modeIndex
  raw.set(encodeName(channel.name), 24)
}

function command(offset: number, operation: 'R' | 'W', payload = ''): Uint8Array {
  return new TextEncoder().encode(
    `AL~EEPEL${offset.toString(16).toUpperCase().padStart(4, '0')}${operation}${payload}\r\n`,
  )
}

async function identify(transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  const request = new TextEncoder().encode('AL~WHO\r\n')
  await transport.write(request)
  await transport.readExact(request.length, 1500)
  const identifier = new TextDecoder()
    .decode(await transport.readExact(9, 1500))
    .trim()
  if (!IDENTIFIERS.has(identifier)) {
    throw new Error('The connected radio does not identify as an Alinco DR735T.')
  }
}

async function resetRadio(transport: RadioTransport): Promise<void> {
  const request = new TextEncoder().encode('AL~RESET\r\n')
  await transport.write(request)
  await transport.readExact(request.length, 1500)
  const response = new TextDecoder().decode(await transport.readExact(4, 1500))
  if (response !== 'OK\r\n') {
    throw new Error('The radio did not acknowledge the DR735T reset command.')
  }
}

export async function readDR735TRadioWithTransport(
  profile: DR735TProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await identify(transport)
  const image = new Uint8Array(MEMORY_SIZE)
  for (let number = 0; number < CHANNEL_COUNT; number += 1) {
    const offset = number * CHANNEL_SIZE
    const request = command(offset, 'R')
    await transport.write(request)
    await transport.readExact(request.length, 1500)
    const hex = new TextDecoder().decode(await transport.readExact(128, 2000))
    await transport.readExact(2, 500)
    for (let index = 0; index < CHANNEL_SIZE; index += 1) {
      const value = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16)
      if (Number.isNaN(value)) {
        throw new Error(
          `Invalid DR735T response at offset 0x${offset.toString(16)}.`,
        )
      }
      image[offset + index] = value
    }
    onProgress?.({
      current: number + 1,
      total: CHANNEL_COUNT,
      label: 'Reading Alinco DR735T memory',
    })
  }
  return { image, reportedModel: profile.model }
}

export async function writeDR735TRadioWithTransport(
  profile: DR735TProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than DR735T memory.')
  }
  await identify(transport)
  const enter = new TextEncoder().encode('AL~DR735J\r\n')
  await transport.write(enter)
  await transport.readExact(enter.length, 1500)
  if (new TextDecoder().decode(await transport.readExact(4, 1500)) !== 'OK\r\n') {
    throw new Error('The radio refused DR735T upload mode.')
  }

  try {
    for (let number = 0; number < CHANNEL_COUNT; number += 1) {
      const offset = number * CHANNEL_SIZE
      const payload = Array.from(
        image.slice(offset, offset + CHANNEL_SIZE),
        (value) => value.toString(16).toUpperCase().padStart(2, '0'),
      ).join('')
      const request = command(offset, 'W', payload)
      await transport.write(request)
      await transport.readExact(request.length, 2000)
      const response = new TextDecoder().decode(
        await transport.readExact(4, 2000),
      )
      if (response !== 'OK\r\n') {
        throw new Error(
          `The radio rejected DR735T memory at offset 0x${offset.toString(16)}.`,
        )
      }
      onProgress?.({
        current: number + 1,
        total: CHANNEL_COUNT,
        label: 'Writing Alinco DR735T memory',
      })
    }
  } finally {
    await resetRadio(transport)
  }
}
